import {IconButton, Label, Link, RelativeTime} from '@primer/react'
import {LinkExternalIcon} from '@primer/octicons-react'
import type {StorageRecord} from '../types'
import styles from './StorageRegistry.module.css'

interface StorageRegistryProps {
  data?: StorageRecord
}

const REGISTRY_MATCHERS = [
  [['azurecr'], 'Azure Container Registry'],
  [['ghcr', 'pkg.github'], 'GitHub Container Registry'],
  [['ecr', 'amazonaws.com'], 'Elastic Container Registry'],
  [['docker.io', 'docker.com'], 'Docker Hub'],
  [['gcr.io', 'pkg.dev'], 'Google Container Registry'],
] as const

function extractRegistryName(url: string): string {
  try {
    const {hostname} = new URL(url, window.location.origin)
    const domain = hostname.replace(/^www\./, '')

    for (const [domains, registry] of REGISTRY_MATCHERS) {
      for (const pattern of domains) {
        if (domain.includes(pattern)) {
          return registry
        }
      }
    }

    // Remove common TLD and return the main part
    const parts = domain.split('.')
    return parts[0] || domain
  } catch {
    // If URL parsing fails, return unknown
    return 'Unknown Registry'
  }
}

export function StorageRegistry({data}: StorageRegistryProps) {
  if (!data) {
    return (
      <div className={styles.Container}>
        <MetadataColumn label="Storage registry">None detected</MetadataColumn>
      </div>
    )
  }

  const statusText = data.status ? data.status.charAt(0).toUpperCase() + data.status.slice(1).toLowerCase() : 'Unknown'
  const statusVariant = data.status?.toLowerCase() === 'active' ? 'success' : 'secondary'
  const registryName = data.source_registry || extractRegistryName(data.registry_url)

  return (
    <div className={styles.Container}>
      <MetadataColumn label="Storage registry">{registryName}</MetadataColumn>

      <MetadataColumn label="Artifact repository">
        {!data.registry_repository_name ? (
          <span>None detected</span>
        ) : (
          <>
            <span>{data.registry_repository_name}</span>
            <Label variant={statusVariant} size="small">
              {statusText}
            </Label>
            {data.artifact_url && (
              <IconButton
                as={Link}
                href={data.artifact_url}
                icon={LinkExternalIcon}
                aria-label="Open repository in new tab"
                variant="invisible"
                size="small"
                className={styles.ExternalLink}
              />
            )}
          </>
        )}
      </MetadataColumn>

      <MetadataColumn label="Last updated">
        <RelativeTime datetime={data.updated_at} />
      </MetadataColumn>
    </div>
  )
}

interface MetadataColumnProps {
  label: string
  children: React.ReactNode
}

function MetadataColumn({label, children}: MetadataColumnProps) {
  return (
    <div className={styles.MetadataColumn}>
      <div className={styles.Label}>{label}</div>
      <div className={styles.Value}>{children}</div>
    </div>
  )
}
